
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  LayoutDashboard, 
  MessageSquare, 
  FileText, 
  Upload, 
  Bell, 
  CheckCircle, 
  AlertTriangle, 
  Zap, 
  Clock, 
  ChevronRight 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusCard } from '@/components/StatusCard';
import { AnimatedButton } from '@/components/AnimatedButton';
import { Sidebar } from '@/components/Sidebar';
import PageTransition from '@/components/PageTransition';
import { useDashboardData } from '@/hooks/useDashboardData';

const Dashboard = () => {
  const { stats, recentActivity, userName, loading } = useDashboardData();

  const getRiskVariant = (level: string | null) => {
    if (!level) return 'default';
    if (level === 'high') return 'danger';
    if (level === 'medium') return 'warning';
    return 'success';
  };

  const formatLastUpdated = (date: string | null) => {
    if (!date) return 'Never';
    const now = new Date();
    const updated = new Date(date);
    const diffMs = now.getTime() - updated.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return updated.toLocaleDateString();
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex-1 overflow-y-auto relative">
        <PageTransition>
          <div className="p-6 md:p-8">
            <header className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Dashboard</h1>
                {loading ? (
                  <Skeleton className="h-5 w-32 mt-1" />
                ) : (
                  <p className="text-muted-foreground">
                    Welcome back{userName ? `, ${userName}` : ''}
                  </p>
                )}
              </div>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full"></span>
              </Button>
            </header>

            {/* Status Cards */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-card border border-border rounded-xl p-6">
                    <Skeleton className="h-5 w-5 mb-3" />
                    <Skeleton className="h-4 w-20 mb-2" />
                    <Skeleton className="h-8 w-16 mb-2" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                ))}
              </div>
            ) : stats.interviewCount === 0 ? (
              <div className="bg-card border border-border rounded-xl p-8 mb-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Get Started with Your Emergency Plan</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Start your first interview to create a personalized emergency preparedness plan for your household.
                </p>
                <Link to="/chat">
                  <AnimatedButton>Start Interview</AnimatedButton>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatusCard
                  icon={CheckCircle}
                  title="Plan Status"
                  value={`${stats.planCompletion}%`}
                  description={
                    stats.planCompletion === 100
                      ? "Your emergency plan is complete"
                      : stats.planCompletion > 0
                      ? "Keep going to complete your plan"
                      : "Start your interview to begin"
                  }
                  variant={stats.planCompletion > 75 ? 'success' : 'primary'}
                  index={0}
                />
                <StatusCard
                  icon={Clock}
                  title="Last Updated"
                  value={formatLastUpdated(stats.lastUpdated)}
                  description="Keep your plan up to date"
                  variant="default"
                  index={1}
                />
                <StatusCard
                  icon={AlertTriangle}
                  title="Risk Assessment"
                  value={stats.riskLevel ? stats.riskLevel.charAt(0).toUpperCase() + stats.riskLevel.slice(1) : 'Not assessed'}
                  description="Based on your location and inputs"
                  variant={getRiskVariant(stats.riskLevel)}
                  index={2}
                />
                <StatusCard
                  icon={Zap}
                  title="Action Items"
                  value={stats.actionItemsCount.toString()}
                  description={
                    stats.actionItemsCount === 0
                      ? "All caught up!"
                      : "Tasks that need your attention"
                  }
                  variant={stats.actionItemsCount > 0 ? 'danger' : 'success'}
                  index={3}
                />
              </div>
            )}

            {/* Quick Actions */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link to={stats.interviewCount > 0 ? "/chat" : "#"} className={`opacity-0 animate-fade-up ${stats.interviewCount === 0 ? 'pointer-events-none' : ''}`} style={{ animationDelay: '100ms' }}>
                  <div className={`h-full bg-card border border-border rounded-xl p-6 transition-all duration-200 ${
                    stats.interviewCount > 0 
                      ? 'hover:border-primary/20 hover:shadow-subtle' 
                      : 'opacity-50 cursor-not-allowed'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                        stats.interviewCount > 0 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                      }`}>
                        <MessageSquare className="h-5 w-5" />
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium mb-1">Continue Interview</h3>
                    <p className="text-sm text-muted-foreground">Complete your emergency plan</p>
                  </div>
                </Link>
                <Link to={stats.interviewCount > 0 ? "/plans" : "#"} className={`opacity-0 animate-fade-up ${stats.interviewCount === 0 ? 'pointer-events-none' : ''}`} style={{ animationDelay: '200ms' }}>
                  <div className={`h-full bg-card border border-border rounded-xl p-6 transition-all duration-200 ${
                    stats.interviewCount > 0 
                      ? 'hover:border-primary/20 hover:shadow-subtle' 
                      : 'opacity-50 cursor-not-allowed'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                        stats.interviewCount > 0 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                      }`}>
                        <FileText className="h-5 w-5" />
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium mb-1">View Plan</h3>
                    <p className="text-sm text-muted-foreground">Access your latest emergency plan</p>
                  </div>
                </Link>
                <Link to={stats.interviewCount > 0 ? "/upload" : "#"} className={`opacity-0 animate-fade-up ${stats.interviewCount === 0 ? 'pointer-events-none' : ''}`} style={{ animationDelay: '300ms' }}>
                  <div className={`h-full bg-card border border-border rounded-xl p-6 transition-all duration-200 ${
                    stats.interviewCount > 0 
                      ? 'hover:border-primary/20 hover:shadow-subtle' 
                      : 'opacity-50 cursor-not-allowed'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                        stats.interviewCount > 0 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                      }`}>
                        <Upload className="h-5 w-5" />
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium mb-1">Upload Images</h3>
                    <p className="text-sm text-muted-foreground">Add important photos to your plan</p>
                  </div>
                </Link>
                <Link to={stats.interviewCount > 0 ? "/dashboard" : "#"} className={`opacity-0 animate-fade-up ${stats.interviewCount === 0 ? 'pointer-events-none' : ''}`} style={{ animationDelay: '400ms' }}>
                  <div className={`h-full bg-card border border-border rounded-xl p-6 transition-all duration-200 ${
                    stats.interviewCount > 0 
                      ? 'hover:border-primary/20 hover:shadow-subtle' 
                      : 'opacity-50 cursor-not-allowed'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                        stats.interviewCount > 0 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                      }`}>
                        <LayoutDashboard className="h-5 w-5" />
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium mb-1">Risk Assessment</h3>
                    <p className="text-sm text-muted-foreground">Review potential risks in your area</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Recent Activity */}
            {!loading && stats.interviewCount > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">Recent Activity</h2>
                </div>
                {recentActivity.length === 0 ? (
                  <div className="bg-card border border-border rounded-xl p-8 text-center">
                    <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                    <p className="text-muted-foreground">No recent activity yet</p>
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-xl overflow-hidden opacity-0 animate-fade-up" style={{ animationDelay: '100ms' }}>
                    <div className="divide-y divide-border">
                      {recentActivity.map((activity) => (
                        <div key={activity.id} className="p-4 flex items-start">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center mr-3 flex-shrink-0">
                            {activity.action.includes('plan') ? (
                              <FileText className="h-4 w-4 text-primary" />
                            ) : (
                              <MessageSquare className="h-4 w-4 text-primary" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium">{activity.action}</p>
                            <p className="text-xs text-muted-foreground mt-1">{activity.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Upgrade Banner */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 opacity-0 animate-fade-up" style={{ animationDelay: '200ms' }}>
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="mb-4 md:mb-0">
                  <h3 className="text-lg font-semibold mb-1">Upgrade to Premium</h3>
                  <p className="text-sm text-muted-foreground">Get access to advanced features and unlimited plan storage</p>
                </div>
                <Link to="/subscription">
                  <AnimatedButton>
                    View Plans
                  </AnimatedButton>
                </Link>
              </div>
            </div>
          </div>
        </PageTransition>
      </div>
    </div>
  );
};

export default Dashboard;
